import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../hooks/useAuth";
import { apiVariable } from "../../api/apiVariable";
import { Box, CircularProgress, Typography, TextField } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import PersistedDataGrid from "../PersistedDataGrid/PersistedDataGrid";
import ResetPasswordPopup from "./ResetPasswordPopup";

export interface UserDto {
  id: string;
  username: string;
  organizationId: string;
  organizationName: string;
  roles: string[];
}

const fetchUsers = async (token: string) => {
  const { data } = await axios.get<UserDto[]>(`${apiVariable}/users`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

const UsersTable = () => {
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: users,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["users"],
    queryFn: () => fetchUsers(token!),
    enabled: Boolean(token),
  });

  useEffect(() => {
    if (token) refetch();
  }, [token, refetch]);

  const filtered = useMemo(() => {
    if (!users) return [];
    return users.filter((u) =>
      [u.username, u.organizationName, ...u.roles].some((f) =>
        f.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [searchTerm, users]);

  const columns: GridColDef[] = [
    { field: "username", headerName: "Username", width: 200 },
    { field: "organizationName", headerName: "Organization", width: 250 },
    {
      field: "roles",
      headerName: "Roles",
      width: 200,
      valueGetter: (params) => (params as string[]).join(", "),
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      width: 80,
      renderCell: (params) => (
        <ResetPasswordPopup user={params.row as UserDto} />
      ),
    },
  ];

  return (
    <Box className="drivers-table-container" height={900}>
      <Box
        className="table-controls"
        sx={{
          display: "flex",
          justifyContent: "right",
          alignItems: "center",
          marginBottom: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: { xs: "100%", sm: 400 },
            minWidth: 0,
            alignItems: "flex-end",
            position: "relative",
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search"
            sx={{ maxWidth: "100%", width: 400 }}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box className="table-error">
          <Typography color="error">Error: {(error as any).message}</Typography>
        </Box>
      ) : (
        <PersistedDataGrid
          tableName="users"
          rows={filtered}
          columns={columns}
          sx={{ height: 700 }}
        />
      )}
    </Box>
  );
};

export default UsersTable;
